// Enquiry to Invoice screens: formatting, error texts and the quotation price preview. Pure functions: no API
// calls, no React. The server recomputes every amount on save (backend utils/e2i.util.ts); these only show the
// figures while the user types.

import type {
  CostType,
  E2IConfig,
  EstimateLine,
  Job,
  JobLine,
  QuotationLine,
  RegisterCode,
} from '../types/e2i';
import { REGISTER_NAMES } from '../types/e2i';
import { fixTwoDigitYear, isDocumentDate } from './documentDate';

/** A company configuration with nothing allowed and no lists (the base the server's answer is laid on). */
export const emptyE2IConfig = (companyId: number): E2IConfig => ({
  companyId,
  enabled: true,
  currency: null,
  vatRegistered: false,
  capabilities: { admin: false, viewAll: false, estimate: false, quote: false, jobs: false, finance: false, register: false, setup: false },
  costTypes: [],
  profitPct: 0,
  vatCodes: [],
  defaultVatCode: null,
  jobTypes: [],
  paymentTerms: [],
  registerCodes: [],
  quoteValidityDays: 30,
  docMode: 'Item',
});

// ---------------------------------------------------------------- numbers

const roundTo = (n: number, places: number): number => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  const f = 10 ** places;
  return (Math.sign(v) * Math.round((Math.abs(v) + Number.EPSILON) * f)) / f;
};

/** Money: 2 decimals, halfway away from zero (as SAP rounds amounts). */
export const round2 = (n: number): number => roundTo(n, 2);
/** Unit prices and costs: 4 decimals. */
export const round4 = (n: number): number => roundTo(n, 4);

const amountFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const quantityFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });

/** 1234.5 -> "1,234.50"; '' for null. */
export const formatAmount = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : amountFormat.format(Number(n));

/** "1,234.50" followed by the company's currency code. */
export const formatMoney = (n: number | null | undefined, currency?: string | null): string => {
  const v = formatAmount(n);
  return v && currency ? `${v} ${currency}` : v;
};

/** 2.5 -> "2.5"; up to 4 decimals. */
export const formatQuantity = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : quantityFormat.format(Number(n));

/** 12.5 -> "12.5%" */
export const formatPct = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : `${quantityFormat.format(Number(n))}%`;

/** A typed number ("1,234.50", " 12 ", "-3", "12." while typing) as a number; null when it is not one. */
export function parseNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const s = String(raw ?? '').replace(/[,\s]/g, '');
  if (!s || !/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** A number for a text box: '' for null, else the plain value. */
export const numberText = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : String(n);

// ---------------------------------------------------------------- dates

/** Today in the browser's time zone, 'YYYY-MM-DD'. */
export function todayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** 'YYYY-MM-DD' plus whole days. */
export function addDaysIso(date: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date || ''));
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + (Number(days) || 0)));
  return d.toISOString().slice(0, 10);
}

/** The date part of a date or timestamp ('' when there is none). */
export const isoDate = (value: string | null | undefined): string => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value ?? '').trim());
  return m ? m[1] : '';
};

/**
 * 'YYYY-MM-DD' -> 'dd/mm/yyyy'. A timestamp with a time zone shows the browser's local date, so something
 * done at 01:00 local time is not dated the day before.
 */
export function formatDate(value: string | null | undefined): string {
  const s = String(value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s) && /(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return formatDate(todayIso(d));
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** A timestamp in the browser's locale. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** A date from a date box: a 2-digit year (0026-09-28) becomes 20yy; other text is kept as typed. */
export function cleanDateInput(value: unknown): string {
  const fixed = fixTwoDigitYear(value);
  return isDocumentDate(fixed) ? fixed : String(value ?? '').trim();
}

/** '' or a usable date ('YYYY-MM-DD', years 2000 to 2099). */
export const dateOrNull = (value: string | null | undefined): string | null => {
  const v = cleanDateInput(value);
  return isDocumentDate(v) ? v : null;
};

// ---------------------------------------------------------------- errors

/** The server's message of a failed request (plus the errors it lists), else the fallback. */
export function errorText(e: any, fallback: string): string {
  const data = e?.response?.data;
  const base = String(data?.message || e?.message || fallback);
  const list: string[] = (Array.isArray(data?.errors) ? data.errors.map(String) : []).filter((x: string) => !base.includes(x));
  return list.length ? `${base}: ${list.join('; ')}` : base;
}

/** Same, for requests made with responseType 'blob' (the error body is a Blob of JSON). */
export async function blobErrorText(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return String(parsed.message);
    } catch {
      /* not JSON */
    }
  }
  return errorText(e, fallback);
}

// ---------------------------------------------------------------- labels

/** "QT-2026-00001/R2" (the number already carrying its revision is left alone). */
export function quotationLabel(no: string | null | undefined, revision: number | null | undefined): string {
  const base = String(no || '').trim();
  const rev = Number(revision) || 0;
  if (!base) return rev ? `Draft R${rev}` : 'Draft';
  if (!rev || /\/R\d+$/i.test(base)) return base;
  return `${base}/R${rev}`;
}

/** "10 · Enquiry open" */
export function registerLabel(code: RegisterCode | null | undefined, name?: string | null, config?: E2IConfig | null): string {
  if (!code) return 'No register status';
  const text = name || config?.registerCodes?.find((r) => Number(r.code) === Number(code))?.name || REGISTER_NAMES[code] || '';
  return text ? `${code} · ${text}` : String(code);
}

export const partyLabel = (p: { code: string; name: string } | null | undefined): string =>
  p ? `${p.code || ''}${p.name ? ` ${p.name}` : ''}`.trim() : '';

/** The job's payment terms by name. */
export const paymentTermsName = (code: number | null | undefined, config: E2IConfig | null | undefined): string => {
  if (code === null || code === undefined) return '';
  const t = config?.paymentTerms?.find((p) => Number(p.code) === Number(code));
  return t ? t.name : String(code);
};

export const costTypeName = (code: string | null | undefined, costTypes: CostType[]): string => {
  if (!code) return 'Priced line';
  return costTypes.find((c) => c.code === code)?.name || code;
};

export const plural = (n: number, word: string, many?: string): string => `${n} ${n === 1 ? word : many || `${word}s`}`;

// ---------------------------------------------------------------- estimate

export const lineAmount = (quantity: number, unitValue: number): number => round2((Number(quantity) || 0) * (Number(unitValue) || 0));

export const estimateTotal = (lines: Array<Pick<EstimateLine, 'quantity' | 'unitCost'>>): number =>
  round2(lines.reduce((sum, l) => sum + lineAmount(l.quantity, l.unitCost), 0));

export interface CostGroup<L> { code: string; name: string; lines: Array<{ line: L; index: number }>; subtotal: number }

/** The lines per cost type, in the company's cost type order (unknown codes last), with their subtotals. */
export function groupByCostType<L extends { costType: string | null; amount: number }>(lines: L[], costTypes: CostType[]): Array<CostGroup<L>> {
  const order = new Map(costTypes.map((c, i) => [c.code, i]));
  const groups = new Map<string, CostGroup<L>>();
  lines.forEach((line, index) => {
    const code = line.costType || '';
    let g = groups.get(code);
    if (!g) {
      g = { code, name: costTypeName(code || null, costTypes), lines: [], subtotal: 0 };
      groups.set(code, g);
    }
    g.lines.push({ line, index });
    g.subtotal = round2(g.subtotal + (Number(line.amount) || 0));
  });
  const rank = (code: string) => (code ? order.get(code) ?? costTypes.length : costTypes.length + 1);
  return Array.from(groups.values()).sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code));
}

// ---------------------------------------------------------------- quotation pricing

/** unit price = unit cost x (1 + markup %) x (1 + profit %), 4 decimals. */
export const priceFromCost = (unitCost: number, markupPct: number, profitPct: number): number =>
  round4((Number(unitCost) || 0) * (1 + (Number(markupPct) || 0) / 100) * (1 + (Number(profitPct) || 0) / 100));

/** The markup of a cost type: the quotation's own, else the company default. */
export const markupOf = (costType: string | null, markups: Record<string, number>, costTypes: CostType[]): number => {
  if (!costType) return 0;
  const own = markups[costType];
  if (own !== undefined && own !== null && Number.isFinite(Number(own))) return Number(own);
  return Number(costTypes.find((c) => c.code === costType)?.markup) || 0;
};

/** A line priced again: cost lines from cost x markup x profit; priced lines keep their unit price. */
export function repriceLine(line: QuotationLine, markups: Record<string, number>, profitPct: number, costTypes: CostType[]): QuotationLine {
  if (!line.costType) {
    const unitPrice = round4(line.unitPrice);
    return { ...line, markupPct: 0, unitPrice, amount: lineAmount(line.quantity, unitPrice) };
  }
  const markupPct = markupOf(line.costType, markups, costTypes);
  const unitPrice = priceFromCost(line.unitCost, markupPct, profitPct);
  return { ...line, markupPct, unitPrice, amount: lineAmount(line.quantity, unitPrice) };
}

export type AdjustMode = 'none' | 'pct' | 'amount';

export interface QuoteFigures {
  linesTotal: number;
  charge: number;
  discount: number;
  subtotal: number;
  vat: number;
  total: number;
  cost: number;
  margin: number;
}

/**
 * subtotal = lines + extra charge (amount, or % of the lines) - discount (amount, or % of lines + charge);
 * VAT = subtotal x rate %; total = subtotal + VAT.
 */
export function quotationFigures(input: {
  lines: Array<Pick<QuotationLine, 'amount' | 'quantity' | 'unitCost'>>;
  chargeMode: AdjustMode;
  chargeValue: number | null;
  discountMode: AdjustMode;
  discountValue: number | null;
  vatRate: number;
}): QuoteFigures {
  const linesTotal = round2(input.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const cost = round2(input.lines.reduce((s, l) => s + lineAmount(l.quantity, l.unitCost), 0));
  const cv = Number(input.chargeValue) || 0;
  const charge = input.chargeMode === 'pct' ? round2(linesTotal * cv / 100) : input.chargeMode === 'amount' ? round2(cv) : 0;
  const dv = Number(input.discountValue) || 0;
  const discount = input.discountMode === 'pct' ? round2((linesTotal + charge) * dv / 100) : input.discountMode === 'amount' ? round2(dv) : 0;
  const subtotal = round2(linesTotal + charge - discount);
  const vat = round2(subtotal * (Number(input.vatRate) || 0) / 100);
  return { linesTotal, charge, discount, subtotal, vat, total: round2(subtotal + vat), cost, margin: round2(subtotal - cost) };
}

/** The pct / amount pair of a saved extra charge or discount as a mode and value (the % wins when both are set). */
export function adjustOf(pct: number | null | undefined, amount: number | null | undefined): { mode: AdjustMode; value: number | null } {
  if (pct !== null && pct !== undefined && Number(pct) !== 0) return { mode: 'pct', value: Number(pct) };
  if (amount !== null && amount !== undefined && Number(amount) !== 0) return { mode: 'amount', value: Number(amount) };
  return { mode: 'none', value: null };
}

/**
 * Several lines as one priced line (quantity 1) carrying their amount and cost, at the place of the first.
 * Returns the new list and the merged line's index.
 */
export function mergeQuotationLines(lines: QuotationLine[], indexes: number[]): { lines: QuotationLine[]; index: number } {
  const picked = Array.from(new Set(indexes)).filter((i) => i >= 0 && i < lines.length).sort((a, b) => a - b);
  if (picked.length < 2) return { lines, index: picked[0] ?? -1 };
  const chosen = picked.map((i) => lines[i]);
  const amount = round2(chosen.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const cost = round4(chosen.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0), 0));
  const uoms = new Set(chosen.map((l) => l.uom || ''));
  const merged: QuotationLine = {
    lineNum: chosen[0].lineNum,
    costType: null,
    description: chosen.map((l) => l.description).filter(Boolean).join(' / ').slice(0, 254),
    quantity: 1,
    uom: uoms.size === 1 ? chosen[0].uom : null,
    unitCost: cost,
    markupPct: 0,
    unitPrice: amount,
    amount,
    salesItem: chosen[0].salesItem,
    account: chosen[0].account,
  };
  const out: QuotationLine[] = [];
  lines.forEach((l, i) => {
    if (i === picked[0]) out.push(merged);
    else if (!picked.includes(i)) out.push(l);
  });
  return { lines: out.map((l, i) => ({ ...l, lineNum: i + 1 })), index: picked[0] };
}

// ---------------------------------------------------------------- jobs and invoice requests

/** Job lines not on an invoice request yet (each job line is invoiced once). */
export const openJobLines = (job: Job | null | undefined): JobLine[] => (job?.lines || []).filter((l) => !l.cirNo && !l.invoiced);

/** A job can be invoiced once its sales order is in SAP. */
export const jobInvoiceable = (job: Job | null | undefined): boolean => !!job && (job.status === 'Ordered' || job.status === 'Completed');

export const fileSizeText = (bytes: number | null | undefined): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** The files the server accepts (the petty cash list: photos and PDFs, 10 MB). */
export const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif';
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** A SAP draft as finance finds it in SAP: its number, with its entry (key) when the two differ. */
export function draftLabel(num: string | null | undefined, entry: number | null | undefined): string {
  const n = String(num ?? '').trim();
  if (n && entry && n !== String(entry)) return `${n} (entry ${entry})`;
  return n || (entry ? String(entry) : '');
}
